/**
 * Formatting of mission start / end / duration for the pick-list detail.
 * Timestamps come from Postgres as text (`2026-10-30 09:12:00.123+01`): they
 * are normalised to ISO 8601 before parsing, because strict engines (Safari)
 * reject the space separator and the short `+HH` offset.
 */
const EMPTY = '—';

export function parseTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = value.trim().replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Intl locale for the dashboard language: `en*` → en-GB (day-first, 24h, never
 * the US month-first form), anything else → it-IT (the i18n fallbackLng).
 */
export function dateLocaleFor(language: string | null | undefined): 'it-IT' | 'en-GB' {
  return language?.toLowerCase().startsWith('en') ? 'en-GB' : 'it-IT';
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

/**
 * Date + time in the browser's local time zone, formatted for the selected
 * language (`language` = `i18n.language`); `—` when missing. Date and time are
 * formatted separately and joined by a space, so it-IT reads `05/03/2026 07:04`.
 */
export function formatDateTime(value: string | null | undefined, language: string | null | undefined): string {
  const d = parseTimestamp(value);
  if (d === null) return EMPTY;
  const locale = dateLocaleFor(language);
  return `${new Intl.DateTimeFormat(locale, DATE_OPTS).format(d)} ${new Intl.DateTimeFormat(locale, TIME_OPTS).format(d)}`;
}

/** `12 min` · `1 h 05 min` · `< 1 min`; `—` unless both ends exist and end ≥ start. */
export function formatDuration(start: string | null | undefined, end: string | null | undefined): string {
  const a = parseTimestamp(start);
  const b = parseTimestamp(end);
  if (a === null || b === null) return EMPTY;
  const ms = b.getTime() - a.getTime();
  if (ms < 0) return EMPTY;
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return '< 1 min';
  if (totalMin < 60) return `${totalMin} min`;
  return `${Math.floor(totalMin / 60)} h ${pad(totalMin % 60)} min`;
}
